import { UserDisplayName, UserOptionLabel } from "@/components";
import { userType } from "@/propTypes";
import { useSuggestUsersQuery } from "@/services";
import { SpaceWithFullWidth } from "@/sharedStyled";
import { faUserMinus, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Select, Space, Typography } from "antd";
import { arrayOf, bool, func, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { AutoCompleteInput } from "../AutoCompleteInput";
import { MinusList } from "../MinusList";
import { getLabel } from "./helper/getLabel";

const { Option } = Select;

const UserSelector = ({
  onUserSelected,
  onUserRemoved,
  existingUsers,
  usersLoading,
  cardTitleKey,
  cardSubTitleKey,
  isAddUser,
}) => {
  const intl = useIntl();

  const [newUsers, setNewUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState();
  const [suggestQuery, setSuggestQuery] = useState({
    query: null,
    viperOnly: false,
  });

  const { data: userSuggestions, isFetching: fetchingSuggestions } =
    useSuggestUsersQuery(suggestQuery, { skip: !suggestQuery?.query });

  useEffect(() => {
    setNewUsers(existingUsers);
  }, [existingUsers]);

  const handleUserSuggestionSelected = (suggestion) => {
    if (newUsers.some(({ userId }) => userId === suggestion)) return;

    const user = userSuggestions.find(
      ({ document: { id } }) => id === suggestion
    ).document;
    onUserSelected(user);
    setNewUsers([...newUsers, { ...user }]);
    setTimeout(() => {
      setSelectedUserId(user.id);
      // Account for transition on antd selector input to avoid a jarring transition
    }, 250);
  };

  const handleUserRemove = (id) => {
    onUserRemoved(id);
    setNewUsers(newUsers.filter((user) => user.id !== id));
  };

  return (
    <Card
      title={
        <Space>
          <FontAwesomeIcon icon={isAddUser ? faUserPlus : faUserMinus} />
          {intl.formatMessage({ id: cardTitleKey })}
        </Space>
      }
      loading={usersLoading}
    >
      <SpaceWithFullWidth direction="vertical" size="small">
        <Typography.Text>
          {intl.formatMessage({ id: cardSubTitleKey })}
        </Typography.Text>
        <AutoCompleteInput
          selectedSuggestionValue={selectedUserId}
          optionLabelProp="label"
          customOptions={userSuggestions?.map(
            ({
              document: {
                id,
                displayName,
                department,
                email,
                jobTitle,
                azureAdAccountEnabled,
              },
            }) => (
              <Option
                key={id}
                label={
                  <UserDisplayName
                    displayName={displayName}
                    userAccountEnabled={azureAdAccountEnabled}
                  />
                }
              >
                <UserOptionLabel
                  displayName={displayName}
                  department={department}
                  email={email}
                  jobTitle={jobTitle}
                />
              </Option>
            )
          )}
          loading={fetchingSuggestions}
          onFetchSuggestions={(queryParameters) =>
            setSuggestQuery({ ...suggestQuery, query: queryParameters })
          }
          onInputChange={handleUserSuggestionSelected}
        />
      </SpaceWithFullWidth>
      <MinusList
        onItemRemove={handleUserRemove}
        items={newUsers?.map((user) => ({
          id: user.id,
          label: getLabel(user),
        }))}
      />
    </Card>
  );
};

UserSelector.propTypes = {
  onUserSelected: func.isRequired,
  onUserRemoved: func.isRequired,
  existingUsers: arrayOf(userType),
  usersLoading: bool,
  cardTitleKey: string.isRequired,
  cardSubTitleKey: string.isRequired,
  isAddUser: bool,
};

export default UserSelector;
